// COP Copilot — the read-side table projection + manifest catalog (CONTRACT:C3-CAPABILITY-PLUGIN.1.0).
//
// Ported from takbot apps/bench/src/broker/tables.ts + data/project.ts (projectEntities /
// projectZones, the zones polygon map, the manifest zones_catalog / entity_values catalog).
//
// CRITICAL WAVE-A CHANGE (the security fold-in): the static `visible:true` filter is GONE.
// takbot's TableRegistry.getTable() returned only rows where a hard-coded `visible:true`
// placeholder was set (data/project.ts seeded every row `visible:true` — NOT a live PDP).
// Here the read source is EXACTLY the envelopes the COP host already delivered through the
// PDP-interposed `ctx.data.subscribe` seam (cop.ts:726-740 — pdp.isVisibleCached): a denied
// envelope is never delivered, so it is never in this working set. This is the live analog
// of pdp.visibleEnvelopes(). The copilot inherits Glomar confinement for free; there is no
// self-applied `visible` placeholder that could drift from the real PDP.
//
// MANIFEST GLOMAR (M2 in the iframe AOA): the zones_catalog / entity_values catalog is
// derived from the VISIBLE working set ONLY — never the full materialized population —
// so a categorical value or zone id that exists only on a DENIED object cannot leak.
//
// Holds NO React, never writes the engine, imports ONLY types from the C3 SDK.

import type { CanonicalEnvelope, Geometry } from "../../../../packages/contracts-ts/src/index.ts";
import type { GeoJsonPolygon, Row } from "./copilotGeoQuery.ts";

/** One flat entity row — the "entities" table shape (ported from data/project.ts EntityRow,
 *  minus the removed static `visible` column: a row that exists here is, by construction,
 *  already visible to the persona). */
export interface EntityRow extends Row {
  id: string;
  kind: string;
  name: string;
  callsign: string | null;
  affiliation: string | null;
  type: string | null;
  domain: string | null;
  /** Split scalar columns (cheap bbox/radius path). Null when geometry is null/non-point. */
  lat: number | null;
  lng: number | null;
  /** The original GeoJSON geometry — kept for within/intersects. Null if non-spatial. */
  geometry: Geometry | null;
  heading_deg: number | null;
  speed_kn: number | null;
  confidence: number | null;
  time: string;
  classification: string;
  releasability: string[];
  mission: string[];
  caveats: string[];
  leakage_profile: string;
}

/** One flat zone row — the "zones" table shape (ported from data/project.ts ZoneRow). */
export interface ZoneRow extends Row {
  id: string;
  kind: string;
  name: string;
  zone_type: string | null;
  geometry: Geometry | null;
  time: string;
  classification: string;
  releasability: string[];
  mission: string[];
  caveats: string[];
  leakage_profile: string;
}

function str(v: unknown): string | null {
  return typeof v === "string" ? v : null;
}
function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

/** Extract a [lng, lat] split from a Point geometry; null for non-point / null geometry. */
function pointLatLng(geometry: Geometry | null): { lat: number | null; lng: number | null } {
  if (geometry && geometry.type === "Point" && Array.isArray(geometry.coordinates)) {
    const c = geometry.coordinates as number[];
    if (typeof c[0] === "number" && typeof c[1] === "number") return { lat: c[1], lng: c[0] };
  }
  return { lat: null, lng: null };
}

/** Project ONE entity envelope (track / asset / observation / alert / sensor) to a flat row. */
export function projectEntity(e: CanonicalEnvelope): EntityRow {
  const { lat, lng } = pointLatLng(e.geometry);
  const p = e.properties ?? {};
  const s = e.security;
  return {
    id: e.id,
    kind: e.kind,
    name: str(p.name) ?? e.id,
    callsign: str((p as Record<string, unknown>).callsign),
    affiliation: str(p.affiliation),
    type: str(p.type),
    domain: str((p as Record<string, unknown>).domain),
    lat,
    lng,
    geometry: e.geometry,
    heading_deg: num((p as Record<string, unknown>).heading_deg),
    speed_kn: num((p as Record<string, unknown>).speed_kn),
    confidence: num(p.confidence),
    time: e.time,
    classification: s.classification,
    releasability: s.releasability,
    mission: s.mission,
    caveats: s.caveats,
    leakage_profile: s.leakage_profile,
  };
}

/** Project ONE geometry/zone envelope (kind: "geometry") to a flat zone row. */
export function projectZone(e: CanonicalEnvelope): ZoneRow {
  const p = e.properties ?? {};
  const s = e.security;
  return {
    id: e.id,
    kind: e.kind,
    name: str(p.name) ?? e.id,
    zone_type: str((p as Record<string, unknown>).zone_type),
    geometry: e.geometry,
    time: e.time,
    classification: s.classification,
    releasability: s.releasability,
    mission: s.mission,
    caveats: s.caveats,
    leakage_profile: s.leakage_profile,
  };
}

/** The declared table view names this projection exposes for Q1 `from`. */
export type TableName = "entities" | "zones";

/** The column set a Q1 query may reference per table (for the host's unknown_column gate). */
export const TABLE_COLUMNS: Record<TableName, string[]> = {
  entities: [
    "id", "kind", "name", "callsign", "affiliation", "type", "domain", "lat", "lng",
    "geometry", "heading_deg", "speed_kn", "confidence", "time", "classification",
    "releasability", "mission", "caveats", "leakage_profile",
  ],
  zones: [
    "id", "kind", "name", "zone_type", "geometry", "time", "classification",
    "releasability", "mission", "caveats", "leakage_profile",
  ],
};

/** Which envelope kinds project into the "zones" table (vs the catch-all "entities"). */
function isZoneKind(kind: CanonicalEnvelope["kind"]): boolean {
  return kind === "geometry";
}

/** THE WORKING SET — a live projection over ONLY the host-delivered (PDP-visible) envelopes.
 *
 *  The copilot capability feeds this exclusively from `ctx.data.subscribe`, which the COP host
 *  has already PDP-interposed (cop.ts:726-740). There is NO `visible:true` filter here and no
 *  way to inject a denied envelope: `ingest()` is the only mutator and the only caller is the
 *  host-filtered subscription. A denied envelope is absent — no row, no count, no marker. */
export class CopilotWorkingSet {
  private readonly entitiesById = new Map<string, EntityRow>();
  private readonly zonesById = new Map<string, ZoneRow>();

  /** File a host-delivered, already-visible envelope. De-dup by id (same id ⇒ update). */
  ingest(env: CanonicalEnvelope): void {
    if (isZoneKind(env.kind)) this.zonesById.set(env.id, projectZone(env));
    else this.entitiesById.set(env.id, projectEntity(env));
  }

  /** Drop an envelope from the working set (e.g. a clearance change re-hid it host-side). */
  remove(id: string): void {
    this.entitiesById.delete(id);
    this.zonesById.delete(id);
  }

  /** EPHEMERALITY / SECURITY-state wipe — empty the ENTIRE working set (both tables). The host
   *  reaches this on a persona switch and logout/teardown (via the api's clearWorkingSet hook in
   *  reset()) and on a query-level DOWNGRADE (via the onEject hook in eject(), cop.ts setQueryLevel)
   *  so NO higher-classification row survives into a lower view (the copilot's read-side analog of
   *  the chat-store rebuild). After clear() the working set is empty until the host-filtered
   *  subscription re-ingests the now-visible census. Glomar holds throughout: only host-delivered
   *  (cleared) envelopes ever return. (CONTRACT:C3-CAPABILITY-PLUGIN.1.0) */
  clear(): void {
    this.entitiesById.clear();
    this.zonesById.clear();
  }

  /** Is `name` a declared table view? */
  has(name: string): name is TableName {
    return name === "entities" || name === "zones";
  }

  /** The declared column set for a table (the broker's field/op validation analog). */
  columns(name: string): string[] | null {
    return name in TABLE_COLUMNS ? TABLE_COLUMNS[name as TableName] : null;
  }

  /** Return the rows of a declared table — ALREADY the visible-only set (no further filter;
   *  there is no `visible` column to filter on). Rows are shallow-cloned so the executor can
   *  never mutate the working set. Unknown table → null (the host maps to `unknown_table`). */
  getTable(name: string): Row[] | null {
    if (name === "entities") return [...this.entitiesById.values()].map((r) => ({ ...r }));
    if (name === "zones") return [...this.zonesById.values()].map((r) => ({ ...r }));
    return null;
  }

  /** zone-id → GeoJsonPolygon, for resolving a Q1 `within`/`intersects` `zone_ref`. Only
   *  VISIBLE zones (i.e. zones in the working set) with a Polygon geometry are exposed — a
   *  denied zone is simply absent and therefore unresolvable (Glomar). Multi-keyed by
   *  id / name / zone_type (lowercased) so a natural ref resolves like the exact id. */
  zonesPolygonMap(): Record<string, GeoJsonPolygon> {
    const map: Record<string, GeoJsonPolygon> = {};
    for (const z of this.zonesById.values()) {
      const g = z.geometry;
      if (g && g.type === "Polygon" && Array.isArray(g.coordinates)) {
        const poly: GeoJsonPolygon = { type: "Polygon", coordinates: g.coordinates as number[][][] };
        for (const key of [z.id, z.name, z.zone_type]) {
          if (typeof key === "string" && key.length > 0) map[key.toLowerCase()] = poly;
        }
      }
    }
    return map;
  }

  /** The `table.manifest` catalog — derived from the VISIBLE working set ONLY (M2 Glomar).
   *  Unlike takbot's manifest() (which enumerated the FULL materialized set and would leak
   *  the distinct values / zone ids of denied objects), this enumerates only what the persona
   *  can already see, so the catalog can never confirm the existence of a denied object. */
  manifest(): Record<string, unknown> {
    const entities = [...this.entitiesById.values()];
    const distinct = (key: keyof EntityRow): string[] => [
      ...new Set(entities.map((e) => e[key]).filter((v): v is string => typeof v === "string")),
    ];
    return {
      tables: Object.keys(TABLE_COLUMNS),
      columns: TABLE_COLUMNS,
      zones_catalog: [...this.zonesById.values()].map((z) => ({ id: z.id, name: z.name, zone_type: z.zone_type })),
      entity_values: {
        affiliation: distinct("affiliation"),
        domain: distinct("domain"),
        kind: distinct("kind"),
        type_examples: distinct("type").slice(0, 24),
      },
      hint:
        "Filter inside a named zone with {op:'within',field:'geometry',zone_ref:'<a zones_catalog id>'}. " +
        "Use the EXACT column names + values above. This catalog reflects ONLY what you are cleared to see.",
    };
  }

  /** Total visible rows across all tables (for diagnostics / the panel's working-set badge). */
  size(): number {
    return this.entitiesById.size + this.zonesById.size;
  }
}
